<script setup>
import CaretDownIcon from '@iconify-vue/ph/caret-down';
import CheckIcon from '@iconify-vue/ph/check';
import { Motion } from 'motion-v';
import {
    ColorAreaArea,
    ColorAreaRoot,
    ColorAreaThumb,
    ColorFieldInput,
    ColorFieldRoot,
    ColorSliderRoot,
    ColorSliderThumb,
    ColorSliderTrack,
    ColorSwatch,
    ColorSwatchPickerItem,
    ColorSwatchPickerItemIndicator,
    ColorSwatchPickerItemSwatch,
    ColorSwatchPickerRoot,
    PopoverContent,
    PopoverPortal,
    PopoverRoot,
    PopoverTrigger,
    isValidColor,
} from 'reka-ui';
import { computed, ref } from 'vue';

const model = defineModel();

const {
    disabled = false,
    size = 'medium',
    presetColors = null,
    withAlpha = false,
    align = 'start',
} = defineProps({
    disabled: {
        type: Boolean,
        required: false,
    },
    size: {
        type: String,
        required: false,
    },
    presetColors: {
        type: Array,
        required: false,
    },
    withAlpha: {
        type: Boolean,
        required: false,
    },
    align: {
        type: String,
        required: false,
    },
});

const FALLBACK_COLOR = '#000000';

/**
 * Damier affiché derrière les couleurs semi-transparentes.
 */
const CHECKERBOARD_STYLE = {
    backgroundImage:
        'linear-gradient(45deg, #d4d4d4 25%, transparent 25%), linear-gradient(-45deg, #d4d4d4 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #d4d4d4 75%), linear-gradient(-45deg, transparent 75%, #d4d4d4 75%)',
    backgroundSize: '8px 8px',
    backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0',
};

const isOpen = ref(false);

const hasValue = computed(
    () => typeof model.value === 'string' && isValidColor(model.value),
);

/**
 * Retire la composante alpha d'une couleur hexadécimale sur 8 caractères.
 */
function stripAlpha(value) {
    return value.length === 9 ? value.slice(0, 7) : value;
}

const color = computed({
    get: () => (hasValue.value ? model.value : FALLBACK_COLOR),
    set: (value) => {
        model.value = withAlpha ? value : stripAlpha(value);
    },
});

const normalizedPresetColors = computed(() =>
    (presetColors ?? []).map((presetColor) => presetColor.toLowerCase()),
);

const selectedPresetColor = computed(() => color.value.toLowerCase());

function selectPresetColor(value) {
    if (typeof value !== 'string') {
        return;
    }

    color.value = value;
}
</script>

<template>
    <PopoverRoot v-model:open="isOpen">
        <PopoverTrigger as-child>
            <slot
                name="trigger"
                :color="color"
                :has-value="hasValue"
                :is-open="isOpen"
            >
                <button
                    type="button"
                    :disabled="disabled"
                    class="flex w-full items-center gap-2 rounded-sm border border-gray-300 bg-white text-left ring-1 duration-150 focus:ring-gray-300 focus:outline-hidden disabled:cursor-not-allowed disabled:bg-gray-100"
                    :class="{
                        '!ring-gray-300': isOpen,
                        'ring-transparent': !isOpen,
                        'h-[50px] px-5 py-3': size === 'medium',
                        'h-[28px] px-3 py-1 text-sm': size === 'small',
                    }"
                >
                    <span
                        class="shrink-0 overflow-hidden rounded-full border border-black/10"
                        :class="{
                            'size-5': size === 'medium',
                            'size-4': size === 'small',
                        }"
                        :style="CHECKERBOARD_STYLE"
                    >
                        <ColorSwatch
                            :color="color"
                            class="block size-full bg-(--reka-color-swatch-color)"
                        />
                    </span>

                    <span v-if="hasValue" class="truncate uppercase">
                        {{ color }}
                    </span>

                    <span v-else class="truncate text-gray-400">
                        Choisir une couleur
                    </span>

                    <CaretDownIcon
                        class="ml-auto size-4 shrink-0 text-gray-500 duration-150"
                        :class="{ 'rotate-180 transform': isOpen }"
                    />
                </button>
            </slot>
        </PopoverTrigger>

        <PopoverPortal>
            <PopoverContent :align="align" :side-offset="6" class="z-50">
                <Motion
                    :initial="{ opacity: 0, scale: 0.96, y: -4 }"
                    :animate="{ opacity: 1, scale: 1, y: 0 }"
                    :transition="{ duration: 0.15 }"
                    class="w-[260px] rounded-sm border border-gray-300 bg-white p-4 shadow-sm"
                >
                    <ColorAreaRoot
                        v-model="color"
                        v-slot="{ style }"
                        color-space="hsb"
                        x-channel="saturation"
                        y-channel="brightness"
                        :disabled="disabled"
                    >
                        <ColorAreaArea
                            :style="style"
                            class="relative h-36 w-full cursor-crosshair rounded-sm border border-black/10"
                        >
                            <ColorAreaThumb
                                class="block size-4 rounded-full border-2 border-white shadow-sm ring-1 ring-black/20 outline-none focus:ring-2 focus:ring-indigo-600"
                                :style="{ backgroundColor: color }"
                            />
                        </ColorAreaArea>
                    </ColorAreaRoot>

                    <ColorSliderRoot
                        v-model="color"
                        channel="hue"
                        color-space="hsb"
                        :disabled="disabled"
                        class="relative mt-4 flex h-4 w-full touch-none items-center select-none"
                    >
                        <ColorSliderTrack
                            class="relative h-3 w-full grow rounded-full border border-black/10"
                        />

                        <ColorSliderThumb
                            class="block size-4 rounded-full border-2 border-white shadow-sm ring-1 ring-black/20 outline-none focus:ring-2 focus:ring-indigo-600"
                        />
                    </ColorSliderRoot>

                    <ColorSliderRoot
                        v-if="withAlpha"
                        v-model="color"
                        channel="alpha"
                        color-space="hsb"
                        :disabled="disabled"
                        class="relative mt-3 flex h-4 w-full touch-none items-center select-none"
                    >
                        <span
                            class="absolute inset-x-0 top-1/2 h-3 -translate-y-1/2 rounded-full"
                            :style="CHECKERBOARD_STYLE"
                        ></span>

                        <ColorSliderTrack
                            class="relative h-3 w-full grow rounded-full border border-black/10"
                        />

                        <ColorSliderThumb
                            class="block size-4 rounded-full border-2 border-white shadow-sm ring-1 ring-black/20 outline-none focus:ring-2 focus:ring-indigo-600"
                        />
                    </ColorSliderRoot>

                    <div class="mt-4 flex items-center gap-2">
                        <ColorFieldRoot
                            v-model="color"
                            :disabled="disabled"
                            class="flex-1"
                        >
                            <ColorFieldInput
                                class="w-full rounded-sm border border-gray-300 px-3 py-1 text-sm uppercase ring-1 ring-transparent duration-150 focus:ring-gray-300 focus:outline-hidden"
                            />
                        </ColorFieldRoot>

                        <span
                            class="size-7 shrink-0 overflow-hidden rounded-sm border border-black/10"
                            :style="CHECKERBOARD_STYLE"
                        >
                            <ColorSwatch
                                :color="color"
                                class="block size-full bg-(--reka-color-swatch-color)"
                            />
                        </span>
                    </div>

                    <ColorSwatchPickerRoot
                        v-if="normalizedPresetColors.length > 0"
                        :model-value="selectedPresetColor"
                        :disabled="disabled"
                        selection-behavior="replace"
                        class="mt-4 flex flex-wrap gap-2"
                        @update:model-value="selectPresetColor"
                    >
                        <ColorSwatchPickerItem
                            v-for="presetColor in normalizedPresetColors"
                            :key="presetColor"
                            :value="presetColor"
                            class="relative size-6 cursor-pointer overflow-hidden rounded-full border border-black/10 duration-150 outline-none hover:scale-110 focus-visible:ring-2 focus-visible:ring-indigo-600"
                        >
                            <ColorSwatchPickerItemSwatch
                                class="block size-full bg-(--reka-color-swatch-color)"
                            />

                            <ColorSwatchPickerItemIndicator
                                class="absolute inset-0 flex items-center justify-center"
                            >
                                <CheckIcon
                                    class="size-4 text-white drop-shadow-[0_0_2px_rgba(0,0,0,0.6)]"
                                />
                            </ColorSwatchPickerItemIndicator>
                        </ColorSwatchPickerItem>
                    </ColorSwatchPickerRoot>
                </Motion>
            </PopoverContent>
        </PopoverPortal>
    </PopoverRoot>
</template>
